/**
 * Monitor.jsx — SuperAdmin Ops Monitor (/monitor). Self-contained page: reads the
 * read-only /api/monitor/* endpoints, styled to the app with a scoped dark-mode
 * toggle and an adjustable timeline that drives the range-based views + reports.
 * Route + nav are SuperAdmin-gated (App.jsx / Layout.jsx).
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getMonitorSnapshot, getMonitorHealth, getMonitorUsers, getMonitorSecrets,
  getMonitorApiMetrics, downloadMonitorReport, getMonitorNotifications, getMonitorClinicAccess,
} from '../../api/monitorClient.js';
import './monitor.css';

const THEME_KEY = 'ae-monitor-theme';
const RANGES = [{ key: '30d', label: '30d' }, { key: 'ytd', label: 'YTD' }, { key: 'all', label: 'All time' }];
const TABS = ['Overview', 'Notifications', 'Clinic Access', 'Users', 'Jobs', 'API', 'Secrets', 'Reports'];

const nfmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const timeStr = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const dayLabel = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const hourLabel = (v) => (v ? new Date(v).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—');

// Poll `fn` every intervalMs while mounted. A changing `key` (e.g. the selected
// timeline range) re-fires the fetch immediately and restarts the interval.
function usePoll(fn, intervalMs, key) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const ref = useRef(fn); ref.current = fn;
  const load = useCallback(async () => {
    try { setState({ data: await ref.current(), error: null, loading: false }); }
    catch (e) { setState((s) => ({ ...s, error: e.message || 'Failed', loading: false })); }
  }, []);
  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive) load(); };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => { alive = false; clearInterval(id); };
  }, [intervalMs, load, key]);
  return { ...state, refresh: load };
}

// Fetch `fn` when `active` becomes true or `key` changes (range/tab driven).
function useLazy(fn, active, key) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const ref = useRef(fn); ref.current = fn;
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    ref.current().then((d) => { if (alive) setState({ data: d, error: null, loading: false }); })
      .catch((e) => { if (alive) setState({ data: null, error: e.message || 'Failed', loading: false }); });
    return () => { alive = false; };
  }, [active, key]);
  return state;
}

const MIN_LABEL_PX = 44;   // width a single date tick needs before ticks start to crowd

// Track a node's rendered width so the bar axis can thin its labels responsively.
function useElementWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setW(e.contentRect.width); });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function Bars({ points, valueKey = 'count', labelKey = 'label', height = 120, color = 'var(--mon-accent)', showValues = false, showLabels = false, fill = false }) {
  const [wrapRef, width] = useElementWidth();
  if (!points.length) return <div className="mon-muted" style={{ fontSize: 12, padding: '18px 0' }}>No data in range.</div>;
  const max = Math.max(1, ...points.map((p) => Number(p[valueKey]) || 0));
  const n = points.length;

  // Axis: label every Nth bar so dates never overlap; if even two won't fit the
  // width, collapse to a single "first – last" range caption instead.
  let axis = null;
  if (showLabels) {
    const fit = width > 0 ? Math.floor(width / MIN_LABEL_PX) : n;
    if (fit < 2) {
      axis = <div className="mon-bars-range">{points[0][labelKey]} – {points[n - 1][labelKey]}</div>;
    } else {
      const step = Math.max(1, Math.ceil(n / fit));
      axis = (
        <div className="mon-bars-axis" aria-hidden="true">
          {points.map((p, i) => {
            const show = i % step === 0 || i === n - 1;
            const edge = i === 0 ? ' mon-bars-tick--first' : i === n - 1 ? ' mon-bars-tick--last' : '';
            return <span key={i} className={`mon-bars-tick${edge}`}>{show ? p[labelKey] : ''}</span>;
          })}
        </div>
      );
    }
  }

  return (
    // fill: stretch to the card's leftover height (card must be .mon-card--fill)
    // instead of the fixed px height — `height` then acts as the floor.
    <div ref={wrapRef} style={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
      <div className={`mon-bars${showValues ? ' mon-bars--values' : ''}`} style={fill ? { flex: 1, minHeight: height } : { height }}>
        {points.map((p, i) => {
          const v = Number(p[valueKey]) || 0;
          return (
            <div key={i} className="mon-bar-col" title={`${p[labelKey] ?? ''}: ${p[valueKey] ?? 0}`}>
              {showValues && <span className="mon-bar-val">{v || ''}</span>}
              <div className="mon-bar" style={{ height: `${v / max * 100}%`, background: color }} />
            </div>
          );
        })}
      </div>
      {axis}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return <div className={`mon-stat${tone ? ` t-${tone}` : ''}`}><div className="v">{value}</div><div className="l">{label}</div></div>;
}

function Empty({ children }) { return <div className="mon-empty">{children}</div>; }

export default function Monitor() {
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const [preset, setPreset] = useState('30d');   // '30d' | 'ytd' | 'all' | 'custom'
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [excludeMigrated, setExcludeMigrated] = useState(false);
  const [tab, setTab] = useState('Overview');
  useEffect(() => { localStorage.setItem(THEME_KEY, theme); }, [theme]);

  // Resolve the selected preset into the { range, label, key } the fetchers use.
  // YTD = Jan 1 of this year → today; All time = unbounded (the server drops the
  // date bound); a custom range only applies once both ends are set and ordered,
  // and falls back to 30d until then.
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const today = new Date().toISOString().slice(0, 10);
  const customValid = preset === 'custom' && from && to && from <= to;
  const { range, rangeLabel, rangeKey } = useMemo(() => {
    if (preset === 'ytd') return { range: { from: yearStart, to: today }, rangeLabel: 'YTD', rangeKey: `ytd:${yearStart}:${today}` };
    if (preset === 'all') return { range: { all: true }, rangeLabel: 'All time', rangeKey: 'all' };
    if (customValid) return { range: { from, to }, rangeLabel: `${dayLabel(from)} – ${dayLabel(to)}`, rangeKey: `c:${from}:${to}` };
    if (preset === 'custom') return { range: { days: 30 }, rangeLabel: 'Custom — pick dates', rangeKey: 'custom-pending' };
    return { range: { days: 30 }, rangeLabel: '30d', rangeKey: 'd:30' };
  }, [preset, customValid, from, to, yearStart, today]);

  // The snapshot's Cases card is window- AND flag-scoped, so its refetch key
  // includes both; range-only views key on rangeKey alone.
  const snap = usePoll(() => getMonitorSnapshot(range, excludeMigrated), 10000, `${rangeKey}:${excludeMigrated ? 'x' : ''}`);
  const health = usePoll(getMonitorHealth, 30000);
  const users = useLazy(() => getMonitorUsers(range), tab === 'Users', `users-${rangeKey}`);
  const api = useLazy(() => getMonitorApiMetrics(range), tab === 'API', `api-${rangeKey}`);
  const secrets = useLazy(getMonitorSecrets, tab === 'Secrets', 'secrets');
  const clinicAccess = useLazy(() => getMonitorClinicAccess(150), tab === 'Clinic Access', 'clinic-access');

  const s = snap.data || {};
  const h = health.data || {};

  return (
    <div className="mon" data-theme={theme}>
      <header className="mon-head">
        <div>
          <h1>Ops Monitor</h1>
          <div className="mon-sub">
            <span className={`mon-live${snap.error ? ' err' : ''}`} /> {snap.error ? 'Disconnected' : 'Live'}
            {s.generatedAt && <> · updated {timeStr(s.generatedAt)}</>}
          </div>
        </div>
        <div className="mon-controls">
          <div className="mon-range" role="group" aria-label="Timeline">
            {RANGES.map((r) => (
              <button key={r.key} className={preset === r.key ? 'active' : ''} onClick={() => setPreset(r.key)}>{r.label}</button>
            ))}
            <button className={preset === 'custom' ? 'active' : ''} onClick={() => setPreset('custom')} title="Pick a custom date range">Custom</button>
          </div>
          {preset === 'custom' && (
            <div className="mon-daterange">
              <input type="date" className="mon-date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
              <span className="mon-date-sep">–</span>
              <input type="date" className="mon-date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
            </div>
          )}
          <label className="mon-check mon-check--ctl" title="Exclude cases imported from SharePoint (sp_item_id) from the case counts">
            <input type="checkbox" checked={excludeMigrated} onChange={(e) => setExcludeMigrated(e.target.checked)} /> Hide SP-migrated
          </label>
          <button className="mon-icon-btn" onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))} title="Toggle dark mode" aria-label="Toggle dark mode">
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <button className="mon-icon-btn" onClick={snap.refresh} title="Refresh" aria-label="Refresh">⟳</button>
        </div>
      </header>

      {/* Health strip */}
      <div className="mon-health">
        <HealthPill label="Database" ok={h.db?.ok} detail={h.db?.latencyMs != null ? `${h.db.latencyMs}ms` : (health.loading ? '…' : '—')} />
        <HealthPill label="Graph" ok={h.graph?.ok} detail={h.graph?.latencyMs != null ? `${h.graph.latencyMs}ms` : '—'} />
        <HealthPill label="Blob storage" ok={h.blob?.ok} detail={h.blob?.container || '—'} />
        <HealthPill label="Notifications 24h" ok={(s.notificationStats?.failed24h ?? 0) === 0} detail={`${nfmt(s.notificationStats?.last24h)} sent · ${nfmt(s.notificationStats?.failed24h)} failed`} />
      </div>

      <nav className="mon-tabs">
        {TABS.map((t) => <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{t}</button>)}
      </nav>

      <div className="mon-panel">
        {tab === 'Overview' && <Overview s={s} loading={snap.loading} rangeLabel={rangeLabel} excludeMigrated={excludeMigrated} />}
        {tab === 'Notifications' && <Notifications range={range} rangeKey={rangeKey} rangeLabel={rangeLabel} />}
        {tab === 'Clinic Access' && <ClinicAccess state={clinicAccess} />}
        {tab === 'Users' && <Users state={users} rangeLabel={rangeLabel} />}
        {tab === 'Jobs' && <Jobs jobs={s.jobs} loading={snap.loading} />}
        {tab === 'API' && <ApiMetrics state={api} rangeLabel={rangeLabel} />}
        {tab === 'Secrets' && <Secrets state={secrets} />}
        {tab === 'Reports' && <Reports range={range} rangeLabel={rangeLabel} excludeMigrated={excludeMigrated} />}
      </div>
    </div>
  );
}

function HealthPill({ label, ok, detail }) {
  const tone = ok === true ? 'ok' : ok === false ? 'bad' : 'unknown';
  return (
    <div className={`mon-pill t-${tone}`}>
      <span className="dot" />
      <div><div className="pl">{label}</div><div className="pd">{detail}</div></div>
    </div>
  );
}

function Overview({ s, loading, rangeLabel, excludeMigrated }) {
  if (loading && !s.caseStats) return <Empty>Loading…</Empty>;
  const byStatus = s.caseStats?.byStatus || {};
  const statusPoints = Object.entries(byStatus).map(([label, count]) => ({ label, count }));
  const nc = s.notificationConfig || {};
  return (
    <div className="mon-grid">
      <section className="mon-card">
        <h3>Cases <span className="mon-muted">· created {rangeLabel}{excludeMigrated ? ' · excl. SP-migrated' : ''}</span></h3>
        <div className="mon-stats">
          <Stat label="Created" value={nfmt(s.caseStats?.total)} />
          {statusPoints.slice(0, 4).map((p) => <Stat key={p.label} label={p.label} value={nfmt(p.count)} />)}
        </div>
      </section>
      <section className="mon-card">
        <h3>Notification rules</h3>
        <div className="mon-stats">
          <Stat label="Enabled" value={nfmt(nc.enabledRules)} />
          <Stat label="Event" value={nfmt(nc.eventRules)} />
          <Stat label="Scheduled" value={nfmt(nc.scheduledRules)} />
          <Stat label="Digest" value={nc.scheduledEnabled ? 'On' : 'Off'} tone={nc.scheduledEnabled ? 'ok' : undefined} />
        </div>
      </section>
      <section className="mon-card mon-card--fill">
        <h3>Notifications · last 24h</h3>
        <Bars
          points={(s.caseStats?.notificationsByHour24h || []).map((x) => ({ label: hourLabel(x.at), count: x.count }))}
          showLabels
          fill
        />
        <div className="mon-muted">{nfmt(s.notificationStats?.last24h)} sent · {nfmt(s.notificationStats?.failed24h)} failed</div>
      </section>
      <section className="mon-card">
        <h3>Top rules · 7d</h3>
        <table className="mon-table"><tbody>
          {(s.notificationStats?.topRules7d || []).map((r, i) => (
            <tr key={i}><td>{r.ruleName || '—'}</td><td className="num">{nfmt(r.count)}</td></tr>
          ))}
          {!(s.notificationStats?.topRules7d || []).length && <tr><td className="mon-muted">No sends yet</td></tr>}
        </tbody></table>
      </section>
      <section className="mon-card wide">
        <h3>Recent config changes</h3>
        <table className="mon-table">
          <thead><tr><th>Key</th><th>By</th><th>When</th></tr></thead>
          <tbody>
            {(s.configAudit || []).slice(0, 8).map((c, i) => (
              <tr key={i}><td>{c.key}</td><td>{c.by || '—'}</td><td>{timeStr(c.at)}</td></tr>
            ))}
            {!(s.configAudit || []).length && <tr><td colSpan={3} className="mon-muted">No changes recorded</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

const NOTIF_PER_PAGE = 20;

// Self-fetching, server-paged feed: only the shown page is loaded (20 rows), and
// filters/search hit the API rather than a big in-memory list. Filter options come
// from the response `facets`, kept stable across loads so the dropdowns don't flicker.
function Notifications({ range, rangeKey, rangeLabel }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [rule, setRule] = useState('');
  const [evType, setEvType] = useState('');
  const [status, setStatus] = useState('');
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [facets, setFacets] = useState(null);

  // Debounce the search box so a request doesn't fire on every keystroke.
  useEffect(() => { const id = setTimeout(() => setQDebounced(q.trim()), 300); return () => clearTimeout(id); }, [q]);
  // Any filter or range change starts back at page 1.
  useEffect(() => { setPage(1); }, [qDebounced, rule, evType, status, rangeKey]);
  // Load the current page whenever it, a filter, or the range changes; stale responses are dropped.
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    getMonitorNotifications({ page, perPage: NOTIF_PER_PAGE, range, q: qDebounced, rule, event: evType, status })
      .then((d) => { if (!alive) return; setState({ data: d, error: null, loading: false }); if (d?.facets) setFacets(d.facets); })
      .catch((e) => { if (alive) setState({ data: null, error: e.message || 'Failed', loading: false }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, qDebounced, rule, evType, status, rangeKey]);

  const { data, error, loading } = state;
  if (error) return <Empty>Couldn’t load notifications — {error}</Empty>;
  if (loading && !data) return <Empty>Loading…</Empty>;

  const rows = data?.rows || [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / NOTIF_PER_PAGE));
  const f = facets || { rules: [], events: [], statuses: [] };
  const anyFilter = !!(qDebounced || rule || evType || status);
  const clear = () => { setQ(''); setRule(''); setEvType(''); setStatus(''); };

  if (!total && !anyFilter) return <Empty>No notifications sent yet.</Empty>;

  const from = total ? (page - 1) * NOTIF_PER_PAGE + 1 : 0;
  const to = Math.min(page * NOTIF_PER_PAGE, total);

  return (
    <section className="mon-card wide">
      <h3>Notifications · {rangeLabel} <span className="mon-muted">({nfmt(total)}{anyFilter ? ' matching' : ''})</span></h3>
      <div className="mon-filters">
        <input className="mon-input" placeholder="Search email, case, rule…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="mon-select" value={rule} onChange={(e) => setRule(e.target.value)}>
          <option value="">All rules</option>
          {f.rules.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="mon-select" value={evType} onChange={(e) => setEvType(e.target.value)}>
          <option value="">All events</option>
          {f.events.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="mon-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {f.statuses.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {anyFilter && <button type="button" className="mon-btn-sm" onClick={clear}>Clear</button>}
      </div>
      <div className="mon-scroll" aria-busy={loading}>
        <table className="mon-table mon-notif-table">
          <thead><tr><th>When</th><th>Case</th><th>Rule</th><th>Event</th><th>Status</th><th>Recipients</th></tr></thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.id} className={n.status === 'failed' ? 'mon-row-failed' : undefined}>
                <td className="mon-nowrap">{timeStr(n.createdAt)}</td>
                <td className="mon-ref">{n.caseRef || '—'}</td>
                <td>{n.ruleName || '—'}</td>
                <td>{n.trigger ? <span className="mon-chip">{n.trigger}</span> : '—'}</td>
                <td><span className={`mon-status t-${n.status === 'failed' ? 'bad' : 'ok'}`}>{n.status}</span></td>
                <td>
                  <div className="mon-recips-cell">
                    {(n.recipients || []).map((r, i) => (
                      <span key={i} className="mon-recip" style={{ background: r.bg, borderColor: r.border, color: r.text }}>{r.email}</span>
                    ))}
                  </div>
                  {n.error && <div className="mon-err">{n.error}</div>}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="mon-muted" style={{ textAlign: 'center', padding: '20px' }}>No notifications match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mon-pager">
        <span className="mon-muted">{total ? `${nfmt(from)}–${nfmt(to)} of ${nfmt(total)}` : '0 of 0'}</span>
        <div className="mon-pager-btns">
          <button type="button" className="mon-btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
          <span className="mon-muted">Page {nfmt(page)} of {nfmt(pages)}</span>
          <button type="button" className="mon-btn-sm" disabled={page >= pages || loading} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next →</button>
        </div>
      </div>
    </section>
  );
}

function Users({ state, rangeLabel }) {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [region, setRegion] = useState('');
  const [source, setSource] = useState('');
  const [lockedOnly, setLockedOnly] = useState(false);

  if (state.loading && !state.data) return <Empty>Loading users…</Empty>;
  if (state.error) return <Empty>Couldn’t load users — {state.error}</Empty>;
  const d = state.data || {};
  const u = d.users || [];
  const sum = d.summary || { total: 0, byRole: {}, byProvenance: {} };

  const joinPts = (d.joinTimeline || []).map((x) => ({ label: dayLabel(x.day), count: x.count }));
  const signPts = (d.usageTimeline || []).map((x) => ({ label: dayLabel(x.day), count: x.sessions }));
  const joinTotal = joinPts.reduce((a, p) => a + (p.count || 0), 0);
  const signTotal = signPts.reduce((a, p) => a + (p.count || 0), 0);

  const roleOpts = [...new Set(u.map((x) => x.roleLabel).filter(Boolean))].sort();
  const regionOpts = [...new Set(u.map((x) => x.provenance).filter(Boolean))].sort();
  const sourceOpts = [...new Set(u.map((x) => x.accessSource).filter(Boolean))].sort();

  const ql = q.trim().toLowerCase();
  const filtered = u.filter((x) =>
    (!ql || (x.email || '').toLowerCase().includes(ql))
    && (!role || x.roleLabel === role)
    && (!region || x.provenance === region)
    && (!source || x.accessSource === source)
    && (!lockedOnly || x.isLocked),
  );
  const anyFilter = ql || role || region || source || lockedOnly;
  const clear = () => { setQ(''); setRole(''); setRegion(''); setSource(''); setLockedOnly(false); };

  return (
    <div className="mon-grid">
      <section className="mon-card">
        <h3>By role</h3>
        <table className="mon-table"><tbody>
          {Object.entries(sum.byRole).sort((a, b) => b[1] - a[1]).map(([k, v]) => <tr key={k}><td>{k}</td><td className="num">{nfmt(v)}</td></tr>)}
        </tbody></table>
      </section>
      <section className="mon-card">
        <h3>By region</h3>
        <table className="mon-table"><tbody>
          {Object.entries(sum.byProvenance).sort((a, b) => b[1] - a[1]).map(([k, v]) => <tr key={k}><td>{k}</td><td className="num">{nfmt(v)}</td></tr>)}
        </tbody></table>
      </section>
      <section className="mon-card">
        <h3>New users · {rangeLabel} <span className="mon-muted">({nfmt(joinTotal)})</span></h3>
        <Bars points={joinPts} color="var(--mon-ok)" showValues showLabels />
      </section>
      <section className="mon-card">
        <h3>Sign-ins · {rangeLabel} <span className="mon-muted">({nfmt(signTotal)})</span></h3>
        <Bars points={signPts} color="var(--mon-accent)" showValues showLabels />
      </section>
      <section className="mon-card wide">
        <h3>Users <span className="mon-muted">({anyFilter ? `${nfmt(filtered.length)} of ${nfmt(sum.total)}` : nfmt(sum.total)})</span></h3>
        <div className="mon-filters">
          <input className="mon-input" placeholder="Search email…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="mon-select" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">All roles</option>
            {roleOpts.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="mon-select" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">All regions</option>
            {regionOpts.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="mon-select" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">All sources</option>
            {sourceOpts.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="mon-check"><input type="checkbox" checked={lockedOnly} onChange={(e) => setLockedOnly(e.target.checked)} /> Locked only</label>
          {anyFilter && <button type="button" className="mon-btn-sm" onClick={clear}>Clear</button>}
        </div>
        <div className="mon-scroll">
          <table className="mon-table">
            <thead><tr><th>Email</th><th>Role</th><th>Region</th><th>Source</th><th>Last active</th></tr></thead>
            <tbody>
              {filtered.map((x) => (
                <tr key={x.email}>
                  <td>{x.email}{x.isLocked && <span className="mon-status t-bad" style={{ marginLeft: 6 }}>locked</span>}</td>
                  <td>{x.roleLabel || '—'}</td>
                  <td>{x.provenance}</td>
                  <td>{x.accessSource}{x.groupName ? ` · ${x.groupName}` : ''}</td>
                  <td>{timeStr(x.lastUnlockedAt)}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={5} className="mon-muted" style={{ textAlign: 'center', padding: '20px' }}>No users match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Jobs({ jobs, loading }) {
  if (loading && !jobs) return <Empty>Loading…</Empty>;
  const items = jobs?.items || [];
  if (jobs?._migrationNeeded) return <Empty>Job history table not present yet.</Empty>;
  if (!items.length) return <Empty>No job runs recorded.</Empty>;
  return (
    <div className="mon-scroll">
      <table className="mon-table">
        <thead><tr><th>Job</th><th>Status</th><th>Started</th><th className="num">Duration</th></tr></thead>
        <tbody>
          {items.map((j) => (
            <tr key={j.id}>
              <td>{j.jobName}</td>
              <td><span className={`mon-status t-${j.status === 'success' ? 'ok' : j.status === 'failed' ? 'bad' : 'warn'}`}>{j.status}</span></td>
              <td>{timeStr(j.startedAt)}</td>
              <td className="num">{j.durationMs != null ? `${(j.durationMs / 1000).toFixed(1)}s` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Clinic-access audit log — requests, approvals, PIN resets, case shares, and
// admin staff changes for the shared clinic mailboxes.
const CLINIC_ACTION_LABEL = {
  request: 'access request', approve: 'approved name', deny: 'denied name',
  pin_reset_request: 'PIN reset requested', pin_reset: 'PIN reset', pin_reset_deny: 'PIN reset denied',
  share: 'case shared', revoke: 'share revoked',
  staff_add: 'staff added', staff_reset: 'PIN reset (admin)', staff_remove: 'staff removed',
  staff_activate: 'staff re-activated', staff_delete: 'staff deleted',
};
const CLINIC_ACTION_TONE = {
  approve: 'ok', staff_add: 'ok', staff_activate: 'ok', pin_reset: 'ok', share: 'ok',
  deny: 'bad', pin_reset_deny: 'bad', staff_remove: 'bad', staff_delete: 'bad', revoke: 'bad',
};

function ClinicAccess({ state }) {
  if (state.loading && !state.data) return <Empty>Loading clinic-access log…</Empty>;
  if (state.error) return <Empty>Couldn’t load the clinic-access log — {state.error}</Empty>;
  const rows = state.data?.entries || [];
  if (!rows.length) return <Empty>No clinic-access actions recorded yet.</Empty>;
  return (
    <div className="mon-scroll">
      <table className="mon-table">
        <thead><tr><th>When</th><th>Action</th><th>Person</th><th>Clinic</th><th>By</th><th>Detail</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{timeStr(r.at)}</td>
              <td><span className={`mon-status t-${CLINIC_ACTION_TONE[r.action] || 'warn'}`}>{CLINIC_ACTION_LABEL[r.action] || r.action}</span></td>
              <td>{r.subjectName || (r.caseId ? `Case AE-${r.caseId}` : '—')}</td>
              <td>{r.clinicName || r.clinicEmail || '—'}</td>
              <td className="mon-muted">{r.actorRole === 'ClinicMailbox' ? 'clinic (self)' : (r.actorEmail || '—')}</td>
              <td className="mon-muted">{[r.caseId && !['share', 'revoke'].includes(r.action) ? `AE-${r.caseId}` : null, r.detail].filter(Boolean).join(' · ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApiMetrics({ state, rangeLabel }) {
  if (state.loading && !state.data) return <Empty>Loading API metrics…</Empty>;
  if (state.error) return <Empty>Couldn’t load API metrics — {state.error}</Empty>;
  const m = state.data || {};
  if (!m.available) {
    return (
      <div className="mon-card">
        <h3>API metrics unavailable</h3>
        <p className="mon-muted">{m.reason || 'Application Insights not configured.'}</p>
        <p className="mon-muted">Set <code>APPINSIGHTS_APP_ID</code> and grant the Function App’s Managed Identity <b>Monitoring Reader</b> on the App Insights resource.</p>
      </div>
    );
  }
  const sm = m.summary || {};
  return (
    <div className="mon-grid">
      <section className="mon-card wide">
        <h3>Requests · {rangeLabel}</h3>
        <div className="mon-stats">
          <Stat label="Total" value={nfmt(sm.total)} />
          <Stat label="Failed" value={nfmt(sm.failed)} tone={(sm.failed || 0) > 0 ? 'bad' : 'ok'} />
          <Stat label="p50" value={sm.p50 != null ? `${sm.p50}ms` : '—'} />
          <Stat label="p95" value={sm.p95 != null ? `${sm.p95}ms` : '—'} />
        </div>
        <Bars points={(m.series || []).map((x) => ({ label: dayLabel(x.timestamp), count: x.count }))} showLabels />
      </section>
      <section className="mon-card wide">
        <h3>Top endpoints</h3>
        <div className="mon-scroll">
          <table className="mon-table">
            <thead><tr><th>Endpoint</th><th className="num">Requests</th><th className="num">Failed</th><th className="num">p95</th></tr></thead>
            <tbody>
              {(m.topEndpoints || []).map((r, i) => (
                <tr key={i}><td>{r.name}</td><td className="num">{nfmt(r.count)}</td><td className="num">{nfmt(r.failed)}</td><td className="num">{r.p95 != null ? `${r.p95}ms` : '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Secrets({ state }) {
  if (state.loading && !state.data) return <Empty>Loading…</Empty>;
  if (state.error) return <Empty>Couldn’t load secrets — {state.error}</Empty>;
  const items = state.data?.items || [];
  if (!items.length) return <Empty>No secrets tracked. Add expiry metadata via the admin_config <code>monitorSecrets</code> key.</Empty>;
  return (
    <div className="mon-scroll">
      <table className="mon-table">
        <thead><tr><th>Secret</th><th>Expires</th><th className="num">Days left</th><th>Owner</th><th>Source</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{it.name}</td>
              <td>{it.expires || '—'}</td>
              <td className="num"><span className={`mon-status t-${it.level === 'expired' || it.level === 'critical' ? 'bad' : it.level === 'warning' ? 'warn' : 'ok'}`}>{it.daysRemaining != null ? it.daysRemaining : '—'}</span></td>
              <td>{it.owner || '—'}</td>
              <td>{it.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const REPORT_DEFS = [
  { kind: 'adoption', label: 'User adoption', desc: 'Onboarding per user + new users by month.' },
  { kind: 'usage', label: 'Usage', desc: 'Daily sign-ins & PIN unlocks over the selected window.' },
  { kind: 'volumes', label: 'Volumes', desc: 'Cases by status / month / treatment / region + notifications.' },
  { kind: 'api', label: 'API calls', desc: 'Request volume, latency & errors (needs App Insights).' },
  { kind: 'responseTimes', label: 'Response times', desc: 'How fast clinics & Supervisors answer their SLA turns (wall-clock), by clinic / region.' },
];

function Reports({ range, rangeLabel, excludeMigrated }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  async function download(kind) {
    setBusy(kind); setErr(null);
    try { await downloadMonitorReport(kind, range, excludeMigrated); }
    catch (e) { setErr(`${kind}: ${e.message || 'download failed'}`); }
    finally { setBusy(null); }
  }
  return (
    <div>
      <div className="mon-muted" style={{ marginBottom: 12 }}>
        Reports use the selected timeline ({rangeLabel}). Exported as Excel (.xlsx).
        {excludeMigrated && ' The Volumes report excludes SP-migrated cases.'}
      </div>
      {err && <div className="mon-err" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="mon-grid">
        {REPORT_DEFS.map((r) => (
          <section className="mon-card" key={r.kind}>
            <h3>{r.label}</h3>
            <p className="mon-muted" style={{ minHeight: 34 }}>{r.desc}</p>
            <button className="mon-btn" onClick={() => download(r.kind)} disabled={busy === r.kind}>
              {busy === r.kind ? 'Preparing…' : 'Download Excel'}
            </button>
          </section>
        ))}
      </div>
    </div>
  );
}
